// Shared list/query state gate. 12+ list pages used `rows = query.data ?? []`
// then `rows.length === 0 ? <empty> : <table>`, conflating loading, error and
// genuinely-empty into one misleading "no data" render (e.g. a green "nothing
// expiring" while the request is still loading or has failed). Wrap the
// empty-vs-data conditional in this gate: it renders error (+ retry) and loading
// distinctly, then defers to the caller's empty / children. The caller keeps its
// own `rows` (computed from query.data) and passes `isEmpty`.
import type { ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudInfoBox } from '@egov-moldova/mud-react';
import { isNormalizedApiError } from '../../app/api-client';

interface QueryStateBaseProps {
  // isLoading = isPending && isFetching (react-query v5): true only on a real
  // in-flight load, NOT for enabled:false queries (which stay pending+idle).
  query: {
    isError: boolean;
    isLoading: boolean;
    data: unknown;
    error?: unknown;
    refetch: () => unknown;
  };
  children: ReactNode;
  loadingText?: string;
  errorText?: string;
}

// The empty pair is all-or-nothing, and deliberately not two independent
// optionals. Almost every caller is a register whose rows are fetched, and for
// those the sentence that says why the screen is blank is the whole of decision
// D3 — an optional `empty` is one an author forgets. A few surfaces have no
// empty state to declare because they cannot be empty: their rows are declared
// in source and an unreachable service yields a row that says so rather than no
// row (SystemConfigPage is the one such screen today). Those omit both, which
// reads as a statement, where `isEmpty={false}` reads as a handled case and is
// not one.
type QueryStateProps = QueryStateBaseProps &
  ({ isEmpty: boolean; empty: ReactNode } | { isEmpty?: never; empty?: never });

// A 403 means the session lacks the role for this resource — not a fetch failure;
// retrying never helps, so show a distinct access-denied message and no Retry.
function isForbidden(err: unknown): boolean {
  return isNormalizedApiError(err) && err.status === 403;
}

export function QueryState({
  query,
  isEmpty,
  empty,
  children,
  loadingText,
  errorText,
}: QueryStateProps): JSX.Element {
  const { t } = useTranslate();

  // Error only when there's nothing to show; on a background-refetch failure with
  // stale data present, keep rendering the data rather than blanking the table.
  if (query.isError && query.data === undefined) {
    if (isForbidden(query.error)) {
      return (
        <MudInfoBox emphasis="strong" role="alert" variant="warning">
          {t('common.forbidden')}
        </MudInfoBox>
      );
    }
    return (
      <MudInfoBox emphasis="strong" role="alert" variant="error">
        <div className="simsm-query-state__error">
          <span>{errorText ?? t('common.load_error')}</span>
          <MudButton
            shape="circular"
            variant="neutral"
            size="sm"
            onClick={() => void query.refetch()}
          >
            {t('common.retry')}
          </MudButton>
        </div>
      </MudInfoBox>
    );
  }

  if (query.isLoading) {
    return (
      <p className="simsm-query-state__loading" role="status" aria-live="polite">
        {loadingText ?? t('common.loading')}
      </p>
    );
  }

  return <>{isEmpty ? empty : children}</>;
}
